import React, { FC, forwardRef, ReactNode } from 'react';
import styled from 'styled-components';

import { HeaderContainer } from '../HeaderContainer/HeaderContainer';

interface CardProps {
  children: ReactNode;
  noBg?: boolean;
  zIndex?: number;
}

interface CardContentProps {
  children: ReactNode;
  minHeight?: string;
}

interface NgCardHeaderProps {
  children: ReactNode;
  filters?: ReactNode;
  hideFilters?: boolean;
  headerContent?: ReactNode;
  noBorder?: boolean;
}

interface NgCardTitleProps {
  title: string;
}

const StyledCard = styled.div<{ $noBg: boolean; $zIndex?: number }>`
  width: 100%;
  min-width: 0;
  overflow: visible;

  z-index: ${({ $zIndex }) => $zIndex ?? 'inherit'};

  border: ${({ theme }) => `1px solid ${theme.palette.cardBorder}`};
  border-radius: ${({ theme }) => theme.radius.card};
  background-color: ${({ theme, $noBg }) => ($noBg ? 'none' : theme.palette.white)};
`;

const StyledCardContent = styled.div<{ $minHeight?: string }>`
  min-height: 100%;
  width: 100%;

  min-height: ${({ $minHeight }) => $minHeight || 'initial'};
  padding: ${({ theme }) => theme.spacing.default};
`;

const StyledNgCardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const StyledNgCardHeaderFilters = styled.div`
  min-height: 51px;
`;

const StyledCardTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.palette.gray};
  font-size: ${({ theme }) => theme.fontSize.default};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`;

export const NgCardTitle: FC<NgCardTitleProps> = ({ title }) => <StyledCardTitle>{title}</StyledCardTitle>;

export const NgCardHeader = forwardRef<HTMLDivElement, NgCardHeaderProps>(
  ({ children, filters, noBorder, hideFilters = false }, ref) => {
    return (
      <StyledNgCardHeader ref={ref}>
        <HeaderContainer noBorder={noBorder}>{children}</HeaderContainer>
        {!hideFilters && <StyledNgCardHeaderFilters>{filters}</StyledNgCardHeaderFilters>}
      </StyledNgCardHeader>
    );
  },
);

NgCardHeader.displayName = 'NgCardHeader';

export const NgCardContent: FC<CardContentProps> = ({ children, minHeight }) => {
  return <StyledCardContent $minHeight={minHeight}>{children}</StyledCardContent>;
};

export const NgCard: FC<CardProps> = ({ children, noBg = false, zIndex }) => {
  return (
    <StyledCard $noBg={noBg} $zIndex={zIndex}>
      {children}
    </StyledCard>
  );
};
